import { useCallback } from 'react';
import { getUnEmbeddedFontsCount, isPasswordProtected } from '@design-stack-ct/assets-sdk';
import { OnBeforeUploadCallback, OnUploadStartCallback } from '@design-stack-vista/upload-components';
import { VistaAsset } from '@design-stack-vista/vista-assets-sdk';
import { ValidationState } from '@vp/upload-and-print-validator';
import { useErrorContext } from 'src/hooks/ErrorContextHooks';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useProcessUpload, useUploadContext } from 'src/hooks/UploadContextHooks';
import { useValidationContext } from 'src/hooks/ValidationContextHooks';
import { usePromptContext } from 'src/providers/PromptContextProvider';
import { Upload } from 'src/services/UploadAndPrintService';
import { clientSideValidation } from 'src/utils/clientSideValidation';
import { UN_EMBEDDED_FONTS_PAGE_LIMIT, UPLOAD_ERRORS_LOCALE_KEY_MAP, ValidationNames } from 'src/utils/Constants';
import { noticeError } from 'src/utils/newRelicUtil';
import { getUnEmbeddedFontPages, getValidationsForProduct, renameFile } from 'src/utils/validationUtil';

export type PageData = {
  UnEmbeddedFontsCount: number;
};

export type UnEmbeddedFonts = {
  pages: Record<string, PageData>;
};

enum FileValidationErrorType {
  PasswordProtected = 'PasswordProtected',
  UnEmbeddedFonts = 'UnEmbeddedFonts',
  Unknown = 'UNKNOWN',
}

export function useFileUpload() {
  const {
    setUpload,
    setIsUploading,
    setUploadRestrictionFailed,
    setFileSize,
    setCurrentUploads,
    currentUploads,
    setFileType,
  } = useUploadContext();
  const { setValidations } = useValidationContext();
  const { productInformation, setProductInformation, completedProductInformation } = useProductContext();
  const { setError } = useErrorContext();
  const { processUpload } = useProcessUpload();
  const {
    setShowProductOptionsPrompt,
    setShowSpecsAndTemplatePrompt,
    setShowValidationErrorPrompt,
  } = usePromptContext();

  const handleBeforeUpload: OnBeforeUploadCallback = useCallback(files => {
    if (files.length === 0) {
      const error = new Error('No files selected for upload');
      noticeError(error, { method: 'handleBeforeUpload', files });
      return;
    }
    // Replacing special characters in the filename with an underscore to validate the filename before creating the asset in Sherbert.
    const validFileName = files[0].name.replace(/[^\w.-]/g, '_');
    files[0] = renameFile(files[0], validFileName);
  }, []);

  const addAsset = useCallback(
    async (file: File, upload: Promise<void | VistaAsset>, signal: AbortSignal) => {
      const isPasswordProtectedPdf = await isPasswordProtected(file);
      if (isPasswordProtectedPdf.PasswordProtected) {
        throw new Error(
          JSON.stringify({
            type: FileValidationErrorType.PasswordProtected,
            message: 'File processing failed, file is password protected.',
          })
        );
      }
      const unEmbeddedFonts: UnEmbeddedFonts = await getUnEmbeddedFontsCount(file);
      const unEmbeddedFontPages = getUnEmbeddedFontPages(unEmbeddedFonts);

      if (unEmbeddedFontPages.length > 0) {
        throw new Error(
          JSON.stringify({
            type: FileValidationErrorType.UnEmbeddedFonts,
            message:
              unEmbeddedFontPages.length < UN_EMBEDDED_FONTS_PAGE_LIMIT
                ? 'Your file has non-embedded fonts in pages.'
                : 'Your file has non-embedded fonts.',
            pages: unEmbeddedFontPages,
            bodyDescription: 'Please embed all fonts in your file before uploading.',
          })
        );
      }

      const asset = await upload;
      if (!asset) {
        throw new Error('asset is undefined');
      }
      // If the upload was aborted, don't perform any actions on the asset
      if (signal.aborted) {
        return;
      }

      await asset.prepare();
      clientSideValidation(file, completedProductInformation, signal, asset.getUrl());
      const newUpload: Upload = {
        fileUri: asset.getUrl(),
        uploadedFileName: asset.data?.info.storage?.fileName,
        thumbnailUrl: asset.thumbnail.getUrl(),
      };
      setUpload(newUpload);
      setIsUploading(false);
      await processUpload(newUpload);
    },
    [processUpload, setIsUploading, setUpload, completedProductInformation]
  );

  const handleUploadStart: OnUploadStartCallback = useCallback(
    async results => {
      setCurrentUploads(results);
      // If results is empty, something went wrong with the upload
      if (results.length < 1) {
        setIsUploading(false);
        return;
      }
      // Only one file can be uploaded at a time
      const { file, upload, signal } = results[0];
      setFileSize(file.size);
      setFileType(file.type);

      try {
        await addAsset(file, upload, signal);
      } catch (error) {
        // If the upload was aborted, don't set any error state
        if (signal.aborted) {
          return;
        }

        if (error instanceof Error) {
          let errorMessage = '';
          let errorType = '';
          let pagesData: number[] = [];
          let modalBodyDescription = '';

          try {
            const errorData = JSON.parse(error.message);
            errorMessage = errorData?.message || error.message;
            errorType = errorData?.type || FileValidationErrorType.Unknown;
            pagesData = errorData?.pages || [];
            modalBodyDescription = errorData?.bodyDescription || '';
          } catch (parseError) {
            errorMessage = error.message;
          }

          const validations = await getValidationsForProduct(productInformation);
          const index = validations.findIndex(v => v.id === ValidationNames.CorruptValidator);

          if (index >= 0) {
            validations[index] = {
              ...validations[index],
              message:
                UPLOAD_ERRORS_LOCALE_KEY_MAP[errorMessage as keyof typeof UPLOAD_ERRORS_LOCALE_KEY_MAP] || errorMessage,
              state: ValidationState.Error,
              details: {
                ...validations[index].details,
                pagesData,
                bodyDescription:
                  UPLOAD_ERRORS_LOCALE_KEY_MAP[modalBodyDescription as keyof typeof UPLOAD_ERRORS_LOCALE_KEY_MAP] ||
                  modalBodyDescription,
              },
            };

            setValidations(validations);
            setIsUploading(false);

            noticeError(errorType, {
              method: 'onUploadStart',
              file: { ...file },
              errorType,
              errorPages: pagesData,
              error: error,
            });
            return;
          }
        }
        const message = (error as Error).message || 'An error occurred during file upload';
        const uploadError = new Error(message);

        setError(uploadError);
        noticeError(uploadError, {
          method: 'onUploadStart',
          file: { ...file },
        });
        setIsUploading(false);
      }
    },
    [
      addAsset,
      productInformation,
      setCurrentUploads,
      setError,
      setFileSize,
      setIsUploading,
      setValidations,
      setFileType,
    ]
  );

  const resetUploader = useCallback(() => {
    setUpload(null);
    setValidations(null);
    setIsUploading(false);
    setUploadRestrictionFailed(false);
    setFileSize(0);
    currentUploads.forEach(({ abort }) => abort());
    setCurrentUploads([]);
    setShowProductOptionsPrompt(false);
    setShowSpecsAndTemplatePrompt(false);
    setShowValidationErrorPrompt(false);
    setProductInformation({ ...productInformation, recommendedProductData: undefined });
  }, [
    currentUploads,
    productInformation,
    setCurrentUploads,
    setFileSize,
    setIsUploading,
    setProductInformation,
    setShowProductOptionsPrompt,
    setShowSpecsAndTemplatePrompt,
    setShowValidationErrorPrompt,
    setUpload,
    setUploadRestrictionFailed,
    setValidations,
  ]);

  return { handleUploadStart, resetUploader, handleBeforeUpload };
}
